import React from "react";
import { createRoot } from "react-dom/client";
import RemstalVoiceWidget from "./components/RemstalVoiceWidget";
import MultiWidgetSpeedDial from "./components/MultiWidgetSpeedDial";
import { WidgetConfig, ThemeConfig, EmbedConfig } from "./hooks/useVoiceWidget";
import type { AvatarConfig } from "./components/MultiWidgetSpeedDial";
import { buildWebComponentConfigFromAttributes } from "./utils/webComponentConfig";
import {
  captureWidgetEvent,
  initWidgetTelemetry,
} from "./services/TelemetryService";
import "./styles.css";

// Web Component
class RemstalVoiceWebComponent extends HTMLElement {
  private root: any;
  private config: WidgetConfig;

  constructor() {
    super();
    this.config = {
      agentId: this.getAttribute("agent-id") || "",
    };
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
    }
  }

  static get observedAttributes() {
    return [
      "agent-id",
      "widget-id",
      "avatar-url",
      "widget-name",
      "welcome-message",
      "theme",
      "theme-button-size",
      "position",
      "debug",
      "auto-start",
      "mode",
      "ui-variant",
      "language",
      "gps-enabled",
    ];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue) {
      this.updateConfig();
      if (this.root) {
        this.render();
      }
    }
  }

  private updateConfig() {
    this.config = buildWebComponentConfigFromAttributes(this);
  }

  private init() {
    this.updateConfig();
    initWidgetTelemetry(this.config);
    this.render();
  }

  private render() {
    if (!this.root) {
      this.root = createRoot(this);
    }

    this.root.render(
      <RemstalVoiceWidget
        config={{
          ...this.config,
          onError: (error) => {
            this.dispatchEvent(new CustomEvent("error", { detail: error }));
          },
          onStateChange: (state) => {
            this.dispatchEvent(
              new CustomEvent("statechange", { detail: state })
            );
          },
        }}
      />
    );
  }
}

// React Widget Class
class ReactRemstalVoiceWidget {
  private config: WidgetConfig;
  private container: HTMLElement;
  private root: any;
  private isEmbedded = false;
  private static instance: ReactRemstalVoiceWidget | null = null;

  constructor(config: WidgetConfig) {
    // Note: API URL is now auto-detected in useVoiceWidget hook
    const newConfig = { ...config };

    this.config = {
      position: "bottom-right",
      offset: { x: 20, y: 20 },
      autoStart: false,
      debug: false,
      ...newConfig,
    };

    this.validateConfig();
    initWidgetTelemetry(this.config);
    this.container = this.createContainer();
    captureWidgetEvent("widget_initialized", {
      widget_id: this.config.widgetId || this.config.agentId || "unknown",
      mode: this.config.mode || "combined",
      ui_variant: this.config.uiVariant || "legacy",
      embedded: Boolean(this.config.embed),
    });
    this.render();
  }

  private validateConfig(): void {
    // API URL is now auto-detected, no manual validation needed
  }

  private createContainer(): HTMLElement {
    if (this.config.embed) {
      this.isEmbedded = true;
      return this.createEmbeddedContainer();
    } else {
      const container = document.createElement("div");
      container.className = "remstal-voice-widget-react";
      document.body.appendChild(container);
      return container;
    }
  }

  private createEmbeddedContainer(): HTMLElement {
    const embedConfig = this.config.embed!;

    if (embedConfig.mode === "button" && embedConfig.targetSelector) {
      const existingButton = document.querySelector(
        embedConfig.targetSelector
      ) as HTMLElement;
      if (!existingButton) {
        throw new Error(
          `Target element not found: ${embedConfig.targetSelector}`
        );
      }
      return existingButton;
    } else if (embedConfig.mode === "container" && embedConfig.targetSelector) {
      const targetContainer = document.querySelector(
        embedConfig.targetSelector
      ) as HTMLElement;
      if (!targetContainer) {
        throw new Error(
          `Target container not found: ${embedConfig.targetSelector}`
        );
      }

      const container = document.createElement("div");
      container.className = "remstal-voice-widget-react";
      targetContainer.appendChild(container);
      return container;
    } else {
      throw new Error("Invalid embed configuration");
    }
  }

  private render(): void {
    this.root = createRoot(this.container);
    this.root.render(
      <RemstalVoiceWidget
        config={{
          ...this.config,
          onStateChange: (state) => {
            if (this.config.onStateChange) {
              this.config.onStateChange(state);
            }
          },
        }}
      />
    );
  }

  public updateTheme(theme: Partial<ThemeConfig>): void {
    this.config.theme = { ...this.config.theme, ...theme };
    this.render();
  }

  public destroy(): void {
    if (this.root) {
      this.root.unmount();
    }
    if (!this.isEmbedded && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    ReactRemstalVoiceWidget.instance = null;
  }

  public getContainer(): HTMLElement {
    return this.container;
  }

  static init(config: WidgetConfig): ReactRemstalVoiceWidget {
    if (ReactRemstalVoiceWidget.instance) {
      // If an instance already exists, destroy it before creating a new one.
      // This prevents duplicate widgets, especially in hot-reloading dev environments.
      ReactRemstalVoiceWidget.instance.destroy();
    }
    ReactRemstalVoiceWidget.instance = new ReactRemstalVoiceWidget(config);
    return ReactRemstalVoiceWidget.instance;
  }
}

// Multi-Widget Speed Dial Configuration
interface MultiWidgetConfig {
  mainAvatar: AvatarConfig;
  subAvatars: [AvatarConfig, AvatarConfig, AvatarConfig];
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  baseConfig?: Partial<WidgetConfig>;
  className?: string;
}

// Multi-Widget React Component Wrapper
class ReactMultiWidgetSpeedDial {
  private container: HTMLElement;
  private root: any;
  private config: MultiWidgetConfig;
  public static instance: ReactMultiWidgetSpeedDial | null = null;

  constructor(config: MultiWidgetConfig) {
    this.config = {
      position: 'bottom-right',
      ...config,
    };

    this.container = this.createContainer();
    this.render();
  }

  private createContainer(): HTMLElement {
    const container = document.createElement("div");
    container.className = "remstal-multi-widget-speed-dial";
    document.body.appendChild(container);
    return container;
  }

  private render(): void {
    this.root = createRoot(this.container);
    this.root.render(
      <MultiWidgetSpeedDial
        mainAvatar={this.config.mainAvatar}
        subAvatars={this.config.subAvatars}
        position={this.config.position}
        baseConfig={this.config.baseConfig}
        className={this.config.className}
      />
    );
  }

  public destroy(): void {
    if (this.root) {
      this.root.unmount();
    }
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    ReactMultiWidgetSpeedDial.instance = null;
  }

  public getContainer(): HTMLElement {
    return this.container;
  }

  static init(config: MultiWidgetConfig): ReactMultiWidgetSpeedDial {
    if (ReactMultiWidgetSpeedDial.instance) {
      ReactMultiWidgetSpeedDial.instance.destroy();
    }
    ReactMultiWidgetSpeedDial.instance = new ReactMultiWidgetSpeedDial(config);
    return ReactMultiWidgetSpeedDial.instance;
  }
}

// Global API
const RemstalVoice = {
  init(config: WidgetConfig): ReactRemstalVoiceWidget {
    return ReactRemstalVoiceWidget.init(config);
  },

  initMultiWidget(config: MultiWidgetConfig): ReactMultiWidgetSpeedDial {
    return ReactMultiWidgetSpeedDial.init(config);
  },

  createWebComponent(tagName: string = "remstal-voice-widget"): void {
    if (!customElements.get(tagName)) {
      customElements.define(tagName, RemstalVoiceWebComponent);
    }
  },

  version: "2.0.0",
};

// Auto-register web component
if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  RemstalVoice.createWebComponent();
}

// Attach to window
if (typeof window !== "undefined") {
  (window as any).RemstalVoice = RemstalVoice;
}

export default RemstalVoice;
export {
  ReactRemstalVoiceWidget,
  ReactMultiWidgetSpeedDial,
  RemstalVoiceWebComponent,
  WidgetConfig,
  MultiWidgetConfig,
  ThemeConfig,
  EmbedConfig,
  AvatarConfig,
};
